import { BuildingMenuType, POI_DISPLAY_TYPE } from '@/enums';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import i18n from '@/locales';
import type { BuildingCategory, BuildingInStoreInfo, BuildingMenu, FloorInfo, KioskSelectedCategory, PoiContent } from '@/services/dto';
import type { ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';
import { sortCategories } from '@/utils/configStore';
import poiUtils from '@/utils/poi';
import { getPoiListImageUrlFromContent } from './poi/poiImage';
import { LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import { SHOP_CATEGORIES } from '@/constants/shop';
import { STORE_CODE_MAP_JAMSIL } from '@/constants/app';

export const DUTY_FREE_TOWN_CODES = ['901', '902', '908', '90G'];
export const DUTY_FREE_BUILDING_ID = 'duty-free';
export const getDutyFreeBuildingName = (lang?: string): string => {
    return i18n.t('shop.duty_free_building', { lng: lang });
};

export const getPoiTownCd = (poi: PoiContent, lang?: string): string => {
    return (lang && poi.contents?.cstrTownCd?.[lang]) || poi.contents?.cstrTownCd?.ko || '';
};

export const getPoiCstrCd = (poi: PoiContent, lang?: string): string => {
    return (lang && poi.contents?.cstrCd?.[lang]) || poi.contents?.cstrCd?.ko || '';
};

export const isDutyFreePoi = (poi: PoiContent, lang?: string): boolean => {
    return DUTY_FREE_TOWN_CODES.includes(getPoiTownCd(poi, lang));
};

interface BranchBuildingConfig {
    names: Record<string, string>;
    townCd: string;
}

export const BRANCH_BUILDINGS: Record<string, BranchBuildingConfig> = {
    '0348': {
        names: { ko: '에비뉴엘', en: 'AVENUEL', ja: 'AVENUEL', 'zh-CN': 'AVENUEL', 'zh-TW': 'AVENUEL' },
        townCd: 'C00401',
    },
    '0409': {
        names: { ko: '월드몰', en: 'World Mall', ja: 'World Mall', 'zh-CN': 'World Mall', 'zh-TW': 'World Mall' },
        townCd: 'C00401',
    },
    '0500': {
        names: { ko: '월드타워', en: 'World Tower', ja: 'ワールドタワー', 'zh-CN': '世界塔', 'zh-TW': '世界塔' },
        townCd: 'C00401',
    },
    '0002': {
        names: { ko: '면세점', en: 'Duty Free', ja: '免税店', 'zh-CN': '免税店', 'zh-TW': '免稅店' },
        townCd: '902',
    },
};

export const isBranchBuildingCode = (id: string | null | undefined): boolean => {
    return !!id && id in BRANCH_BUILDINGS;
};

export const getBranchBuildingName = (cstrCd: string, lang: string): string => {
    const branch = BRANCH_BUILDINGS[cstrCd];
    return branch?.names?.[lang] || branch?.names?.ko || '';
};

export const isBranchStorePoi = (poi: PoiContent, branchCstrCd: string, lang?: string): boolean => {
    const branch = BRANCH_BUILDINGS[branchCstrCd];
    if (!branch) return false;
    return (branchCstrCd === '0002' || getPoiCstrCd(poi, lang) === branchCstrCd) && getPoiTownCd(poi, lang) === branch.townCd;
};

export const isValidLottePoi = (poi: PoiContent): boolean => {
    const hasCstrCd = !!poi.contents?.cstrCd?.ko;
    const hasCstrTownCd = !!poi.contents?.cstrTownCd?.ko;
    return hasCstrCd && hasCstrTownCd;
};

export const shouldShowBuildingName = (
    buildings: BuildingInStoreInfo[] | undefined,
    storeCode: string | null | undefined,
    language?: string,
    pois?: PoiContent[]
): boolean => {
    if (Array.isArray(buildings) && buildings.length > 1) return true;
    if (!Array.isArray(buildings) || buildings.length === 0) return false;
    if (storeCode === STORE_CODE_MAP_JAMSIL) return true;
    const allPois = pois ?? buildings.flatMap((b) => b.poiContents ?? []);
    return allPois.some((poi) => isDutyFreePoi(poi, language));
};

export const getPoiBuildingName = (poi: PoiContent, lang: string, defaultBuildingName: string, storeCode?: string | null): string => {
    if (storeCode === STORE_CODE_MAP_JAMSIL) {
        const cstrCd = getPoiCstrCd(poi, lang);
        const branchName = getBranchBuildingName(cstrCd, lang);
        if (branchName) return branchName;
    }
    if (isDutyFreePoi(poi, lang)) {
        return getDutyFreeBuildingName(lang);
    }
    return defaultBuildingName;
};

const optionAllCategory: KioskSelectedCategory = {
    clientCode: 'all',
    name: 'common.text.all_category',
    id: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};

const getFloorCode = (floor: FloorInfo): string => (floor as FloorInfo & { floor_code?: string }).floor_code ?? floor.floorCode;

export const getSearchMenu = (buildingMenus: Record<string, BuildingMenu[]> | null | undefined, language: string): BuildingMenu | undefined => {
    const menus = buildingMenus?.[language] ?? [];
    return menus.find((menu) => menu.menuType === BuildingMenuType.SEARCH);
};

export const getShopCategories = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage: string
): KioskSelectedCategory[] => {
    const searchMenu = getSearchMenu(buildingMenus, language) ?? getSearchMenu(buildingMenus, mainLanguage);
    const selectedCategories = searchMenu?.selectedCategories ?? [];

    if (!selectedCategories.length) return [optionAllCategory];

    return sortCategories([optionAllCategory, ...selectedCategories]);
};

export const getListCategories = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage: string
): KioskSelectedCategory[] => {
    const searchMenu = getSearchMenu(buildingMenus, language) ?? getSearchMenu(buildingMenus, mainLanguage);
    const selectedCategories = searchMenu?.listCategory ?? [];

    if (!selectedCategories.length) return [optionAllCategory];

    return sortCategories([optionAllCategory, ...selectedCategories]);
};

const findPoiShopCategory = (poiCategoryCodes: string[], shopCategories: KioskSelectedCategory[]): BuildingCategory | undefined => {
    const matchedCategory = shopCategories.find((category) => category.clientCode !== 'all' && poiCategoryCodes.includes(category.categoryCode));

    return matchedCategory as BuildingCategory | undefined;
};

export const resolveShopPoiContents = (searchMenu: BuildingMenu | undefined, selectedBuildingId: string | null, searchMode: SHOP_SEARCH_MODE): PoiContent[] => {
    if (!searchMenu) return [];

    const isFloorModeWithBuilding = searchMode === SHOP_SEARCH_MODE.SEARCH_BY_FLOOR && selectedBuildingId && selectedBuildingId !== 'all';

    if (isFloorModeWithBuilding && searchMenu.poiContentBuildings?.length) {
        const match = searchMenu.poiContentBuildings.find((b) => b.id === selectedBuildingId);
        return match?.poiContents ?? searchMenu.poiContents ?? [];
    }

    return searchMenu.poiContents ?? [];
};

export const convertPoiContentToSlide = (
    lang: string,
    buildings: BuildingInStoreInfo[],
    pois?: PoiContent[],
    searchText?: string,
    shopCategories?: KioskSelectedCategory[],
    buildingMenus?: Record<string, BuildingMenu[]> | null,
    storeCode?: string | null
): PoiContentSlide[] => {
    const buildingById = new Map(buildings.map((b) => [b.studioBuildingId, b]));

    const allPois: { poi: PoiContent; building: BuildingInStoreInfo }[] = pois
        ? pois.flatMap((poi) => {
              const building = buildingById.get(poi.studioBuildingId);
              return building ? [{ poi, building }] : [];
          })
        : [];

    return allPois?.map(({ poi, building }) => {
        const formatData = {
            name: poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'],
            shopUrl: getPoiListImageUrlFromContent(poi, lang),
        };

        const name = formatData.name || '';
        const shopUrl = formatData.shopUrl || '';
        const floor = poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []);
        const isDutyFree = isDutyFreePoi(poi, lang);
        const defaultBuildingName = poiUtils.getBuildingLocalizedName(building, lang);
        const buildingName = getPoiBuildingName(poi, lang, defaultBuildingName, storeCode);
        const poiCategoryCodes = poiUtils.getPoiCategoryCodes(poi);

        const finalCategory = shopCategories?.length ? findPoiShopCategory(poiCategoryCodes, shopCategories) : undefined;

        const searchKeyword = poi.managedContents?.find((item) => item.display_type === POI_DISPLAY_TYPE.KEY_WORDS)?.content;

        const matchingKeyword = searchText ? poiUtils.extractMatchingKeyword(searchKeyword || {}, searchText, lang) : undefined;
        const categories = shopCategories || [];

        const fnbRootCodes = new Set(categories.filter((c) => c.clientCode === LOTTE_CATEGORY_FIELD.SMALL_CLASSIFICATION_CODE).map((c) => c.categoryCode));

        const facilityRootCodes = new Set(
            categories.filter((c) => c.clientCode === LOTTE_CATEGORY_FIELD.MEDIUM_CLASSIFICATION_CODE).map((c) => c.categoryCode)
        );

        const validFnbCodes = new Set(categories.filter((c) => fnbRootCodes.has(c.parentCategoryCode)).map((c) => c.categoryCode));

        const validFacilityCodes = new Set(categories.filter((c) => facilityRootCodes.has(c.parentCategoryCode)).map((c) => c.categoryCode));

        const menuFacilityCategories = buildingMenus?.[lang]?.find((item) => item.menuCode === BuildingMenuType.FACILITIES)?.listCategory || [];
        menuFacilityCategories.forEach((item) => validFacilityCodes.add(item.categoryCode));

        const isFnb = poi.categories.some((code) => validFnbCodes.has(code));
        const isFacility = poi.categories.some((code) => validFacilityCodes.has(code));

        const poiType = isFnb ? 'fnb' : isFacility ? 'facility' : 'normal';

        const lrclsCd = poi.contents?.lrclsCd;
        const categoryCode = lrclsCd ? lrclsCd[lang] || lrclsCd['ko'] || '' : '';
        const categoryName = SHOP_CATEGORIES[categoryCode] ?? '';

        return {
            id: poi.id,
            name,
            keyword: matchingKeyword,
            buildingName,
            floor,
            url: shopUrl,
            data: poi,
            poiType: poiType,
            category: finalCategory ?? undefined,
            categoryName,
            isDutyFreePoi: isDutyFree,
        };
    });
};

const matchFloor = (studioFloorId: string, selectedFloor: ShopFilterConfig['selectedFloor']): boolean => {
    if (selectedFloor?.id === 'all') return true;

    if (Array.isArray(selectedFloor?.data)) {
        return (selectedFloor.data as string[]).includes(studioFloorId);
    }

    if (selectedFloor?.data) {
        return studioFloorId === getFloorCode(selectedFloor.data as FloorInfo);
    }

    return false;
};

export const filterByFloor = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedBuildingId, selectedFloor, buildings, language } = config;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(shop.data, language)) return false;
        return matchFloor(shop.data.studioFloorId, selectedFloor);
    }

    if (isBranchBuildingCode(selectedBuildingId)) {
        if (!isBranchStorePoi(shop.data, selectedBuildingId!, language)) return false;
        return matchFloor(shop.data.studioFloorId, selectedFloor);
    }

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(shop.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return shop.data.studioBuildingId === building.studioBuildingId;
        }

        return shop.data.studioFloorId === getFloorCode(selectedFloor?.data as FloorInfo) && shop.data.studioBuildingId === building.studioBuildingId;
    }

    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(shop.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedCategory, selectedBuildingId, buildings, categoryCodesForFilter, language } = config;

    if (!selectedCategory) return false;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(shop.data, language)) return false;
    } else if (isBranchBuildingCode(selectedBuildingId)) {
        if (!isBranchStorePoi(shop.data, selectedBuildingId!, language)) return false;
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(shop.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    if (!categoryCodesForFilter?.length) return false;

    const poiCategoryCodes = poiUtils.getPoiCategoryCodes(shop.data);
    return categoryCodesForFilter.some((code) => poiCategoryCodes.includes(code));
};

export const filterByText = (shop: PoiContentSlide, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en'): boolean => {
    const { searchText, language, selectedBuildingId, buildings, keywordSearchEnabled } = config;

    if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
        if (!isDutyFreePoi(shop.data, language)) return false;
    } else if (isBranchBuildingCode(selectedBuildingId)) {
        if (!isBranchStorePoi(shop.data, selectedBuildingId!, language)) return false;
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        if (isDutyFreePoi(shop.data, language)) return false;

        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (!searchText?.trim()) return true;

    const nameKeyboard = shop.data.name[keyboardLanguage] || '';
    const keywordKeyboard = (shop.data.contents?.crltSrchKwrd?.['ko'] || '').replaceAll('#', '');
    const nameDefault = shop.data.name[language] || '';
    const keywordDefault = (shop.data.contents?.crltSrchKwrd?.['ko'] || '').replaceAll('#', '');
    const normalizedSearchText = searchText.trim();

    if(keywordSearchEnabled) {
    return (
        poiUtils.searchByText(nameKeyboard, normalizedSearchText) ||
        poiUtils.searchByText(keywordKeyboard, normalizedSearchText) ||
        poiUtils.searchByText(nameDefault, normalizedSearchText) ||
        poiUtils.searchByText(keywordDefault, normalizedSearchText)
        );
    }
    return poiUtils.searchByText(nameKeyboard, normalizedSearchText) || poiUtils.searchByText(nameDefault, normalizedSearchText);

};

export const createShopFilter = (searchMode: SHOP_SEARCH_MODE, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en') => {
    const filterMap = {
        [SHOP_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
        [SHOP_SEARCH_MODE.SEARCH_BY_TEXT]: filterByText,
    };

    const filterFunction = filterMap[searchMode];

    return (shop: PoiContentSlide) => {
        return filterFunction ? filterFunction(shop, config, keyboardLanguage) : true;
    };
};

const shopUtils = {
    convertPoiContentToSlide,
    createShopFilter,
    filterByFloor,
    filterByCategory,
    filterByText,
    getSearchMenu,
    getShopCategories,
    resolveShopPoiContents,
};

export default shopUtils;
